import type { DashboardMapItem } from '@/core/interfaces/Dashboard';
import {
  MAP_CALLOUT,
  type CalloutMode,
} from '@/core/constants/dashboard-map.constants';
import {
  resolveCalloutDisplayMode,
  resolveCalloutMode,
  shouldRenderCallout,
} from '@/components/dashboard/map/composables/useMapCalloutMode';

export interface Point {
  x: number;
  y: number;
}

export interface CalloutRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export type CalloutSide = 'left' | 'right';

export interface ProjectedMapItem {
  marker: DashboardMapItem;
  x: number;
  y: number;
  index: number;
}

export interface CalloutLayout {
  marker: DashboardMapItem;
  markerX: number;
  markerY: number;
  side: CalloutSide;
  card: CalloutRect;
  path: [number, number][];
  displayMode: CalloutMode;
}

export interface CalloutLayoutOptions {
  selectedMarkerId?: number | null;
  hoveredMarkerId?: number | null;
  calloutMode?: CalloutMode;
}

const STATUS_PRIORITY: Record<string, number> = {
  critical: 0,
  warning: 1,
  healthy: 2,
  inactive: 3,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

function intersects(a: CalloutRect, b: CalloutRect, gap = MAP_CALLOUT.gap): boolean {
  return !(
    a.left + a.width + gap <= b.left ||
    b.left + b.width + gap <= a.left ||
    a.top + a.height + gap <= b.top ||
    b.top + b.height + gap <= a.top
  );
}

function getCardSize(
  displayMode: CalloutMode,
  isSelected: boolean,
  isHovered: boolean
): { width: number; height: number } {
  if (isHovered) {
    return {
      width: MAP_CALLOUT.hoverWidth,
      height: MAP_CALLOUT.hoverHeight,
    };
  }

  if (displayMode === 'label-only' && !isSelected) {
    return {
      width: MAP_CALLOUT.labelOnlyWidth,
      height: MAP_CALLOUT.labelOnlyHeight,
    };
  }

  if (isSelected || displayMode === 'full') {
    return {
      width: MAP_CALLOUT.selectedWidth,
      height: MAP_CALLOUT.selectedHeight,
    };
  }

  return {
    width: MAP_CALLOUT.width,
    height: MAP_CALLOUT.height,
  };
}

/**
 * North→south, then west→east — stable order for alternating lanes.
 */
function sortForLanes(
  items: ProjectedMapItem[],
  selectedMarkerId: number | null
): ProjectedMapItem[] {
  return [...items].sort((a, b) => {
    const aSelected = selectedMarkerId === a.marker.id ? 0 : 1;
    const bSelected = selectedMarkerId === b.marker.id ? 0 : 1;
    if (aSelected !== bSelected) {
      return aSelected - bSelected;
    }

    const statusDiff =
      (STATUS_PRIORITY[a.marker.status] ?? 4) -
      (STATUS_PRIORITY[b.marker.status] ?? 4);
    if (statusDiff !== 0) {
      return statusDiff;
    }

    if (a.y !== b.y) {
      return a.y - b.y;
    }

    return a.x - b.x;
  });
}

/**
 * Alternating left/right lane assignment (override via labelSide).
 */
function resolveLaneSide(
  item: ProjectedMapItem,
  laneIndex: number
): CalloutSide {
  const override = item.marker.labelSide;
  if (override === 'left' || override === 'right') {
    return override;
  }
  return laneIndex % 2 === 0 ? 'left' : 'right';
}

function laneLeft(
  side: CalloutSide,
  size: { width: number },
  viewport: { width: number }
): number {
  if (side === 'left') {
    return MAP_CALLOUT.laneInset;
  }
  return viewport.width - MAP_CALLOUT.laneInset - size.width;
}

/**
 * Place card in a fixed left/right lane, staggered vertically from marker Y.
 */
function placeInLane(
  item: ProjectedMapItem,
  side: CalloutSide,
  size: { width: number; height: number },
  laneRects: CalloutRect[],
  viewport: { width: number; height: number }
): CalloutRect {
  const { gap, maxAttempts, viewportPadding, staggerStep } = MAP_CALLOUT;
  const left = laneLeft(side, size, viewport);
  let top = item.y - size.height / 2;
  let attempts = 0;

  let rect: CalloutRect = {
    left,
    top,
    width: size.width,
    height: size.height,
  };

  while (
    laneRects.some((existing) => intersects(existing, rect)) &&
    attempts < maxAttempts
  ) {
    top += size.height + gap;
    rect = { ...rect, top };
    attempts += 1;
  }

  if (rect.top + rect.height > viewport.height - viewportPadding) {
    top = item.y - size.height / 2;
    attempts = 0;
    rect = { left, top, width: size.width, height: size.height };

    while (
      laneRects.some((existing) => intersects(existing, rect)) &&
      attempts < maxAttempts
    ) {
      top -= size.height + gap;
      rect = { ...rect, top };
      attempts += 1;
    }
  }

  // Fine stagger so cards in the same lane don't share identical Y centers.
  if (laneRects.length > 0) {
    const last = laneRects[laneRects.length - 1];
    if (Math.abs(rect.top - last.top) < staggerStep) {
      rect = { ...rect, top: last.top + size.height + gap };
    }
  }

  return {
    ...rect,
    left: clamp(
      rect.left,
      viewportPadding,
      viewport.width - rect.width - viewportPadding
    ),
    top: clamp(
      rect.top,
      viewportPadding,
      viewport.height - rect.height - viewportPadding
    ),
  };
}

/**
 * Elbow leader: marker → short offset → card edge on its lane.
 */
export function buildLeaderPath(
  marker: Point,
  card: CalloutRect,
  side: CalloutSide
): Point[] {
  const cardAnchorY = card.top + card.height / 2;
  const elbow = MAP_CALLOUT.elbowOffset;

  if (side === 'right') {
    return [
      marker,
      { x: marker.x + elbow, y: cardAnchorY },
      { x: card.left, y: cardAnchorY },
    ];
  }

  return [
    marker,
    { x: marker.x - elbow, y: cardAnchorY },
    { x: card.left + card.width, y: cardAnchorY },
  ];
}

/**
 * Lane-based alternating left/right layout with vertical stagger.
 * Keeps the central map clear of stacked callout columns.
 */
export function resolveCalloutLayout(
  items: ProjectedMapItem[],
  viewport: { width: number; height: number },
  options: CalloutLayoutOptions | number | null = null
): CalloutLayout[] {
  const opts: CalloutLayoutOptions =
    typeof options === 'number' || options === null
      ? { selectedMarkerId: options }
      : options;

  const selectedMarkerId = opts.selectedMarkerId ?? null;
  const hoveredMarkerId = opts.hoveredMarkerId ?? null;
  const mode = opts.calloutMode ?? resolveCalloutMode(items.length);

  const visible = items.filter((item) =>
    shouldRenderCallout(
      item.marker,
      mode,
      selectedMarkerId,
      hoveredMarkerId
    )
  );
  const sorted = sortForLanes(visible, selectedMarkerId);
  const leftLane: CalloutRect[] = [];
  const rightLane: CalloutRect[] = [];
  const layouts: CalloutLayout[] = [];

  sorted.forEach((item, laneIndex) => {
    const displayMode = resolveCalloutDisplayMode(
      mode,
      item.marker,
      selectedMarkerId,
      hoveredMarkerId
    );
    const isSelected = selectedMarkerId === item.marker.id;
    const isHovered = hoveredMarkerId === item.marker.id;
    const size = getCardSize(displayMode, isSelected, isHovered);
    const side = resolveLaneSide(item, laneIndex);
    const laneRects = side === 'left' ? leftLane : rightLane;
    const card = placeInLane(item, side, size, laneRects, viewport);
    laneRects.push(card);

    const pathPoints =
      displayMode === 'label-only' && !isSelected && !isHovered
        ? []
        : buildLeaderPath({ x: item.x, y: item.y }, card, side);

    layouts.push({
      marker: item.marker,
      markerX: item.x,
      markerY: item.y,
      side,
      card,
      path: pathPoints.map((point) => [point.x, point.y]),
      displayMode,
    });
  });

  return layouts;
}

export function screenPathToGeoPath(
  map: { unproject: (point: [number, number]) => { lng: number; lat: number } },
  path: [number, number][]
): [number, number][] {
  return path.map(([x, y]) => {
    const { lng, lat } = map.unproject([x, y]);
    return [lng, lat];
  });
}

export function hexToRgba(
  hex: string,
  alpha: number
): [number, number, number, number] {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return [r, g, b, alpha];
}

export function useMapCalloutLayout() {
  return {
    resolveCalloutLayout,
    resolveCalloutMode,
    buildLeaderPath,
    screenPathToGeoPath,
    hexToRgba,
  };
}
